/**
 * Stream chat messages from the backend
 * @param {string} prompt - The user's message
 * @param {Object} options - Callback options
 * @param {Function} options.onToken - Called for each token received
 * @param {Function} options.onDone - Called when stream completes
 * @param {Function} options.onError - Called on error
 * @returns {Object} Object with cancel method to abort the stream
 */
export function streamChat(prompt, { onToken, onDone, onError } = {}) {
  const controller = new AbortController()

  // Ensure we use the global encodeURIComponent function
  const encode = globalThis.encodeURIComponent || window.encodeURIComponent
  const url = `/chat?prompt=${encode(prompt)}`

    // Immediately invoked async function
    ; (async () => {
      try {
        const res = await fetch(url, {
          method: 'GET',
          headers: {
            // Keep broad accept header; no special SSE handling here.
            Accept: '*/*'
          },
          signal: controller.signal
        })

        if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`)

        const contentType = res.headers.get('content-type') || ''
        const reader = res.body.getReader()
        const decoder = new TextDecoder('utf-8')

        if (!contentType.includes('text/event-stream')) {
          // Plain streaming
          while (true) {
            const { value, done } = await reader.read()
            if (done) break
            const chunk = decoder.decode(value, { stream: true })
            if (chunk) onToken && onToken(chunk)
          }
          const tail = decoder.decode()
          if (tail) onToken && onToken(tail)
          onDone && onDone()
          return
        }

        // SSE parsing (minimal)
        let buffer = ''
        while (true) {
          const { value, done } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })

          // Split on double newline boundaries
          const events = buffer.split(/\n\n/)
          buffer = events.pop() || ''

          for (const evt of events) {
            const lines = evt.split(/\n/)
            const dataLines = []
            for (let raw of lines) {
              if (!raw) continue
              const line = raw.replace(/\r$/, '')
              if (line.startsWith('data:')) {
                // Keep payload exactly (remove just the 'data:' prefix)
                dataLines.push(line.slice(5))
              }
            }
            if (!dataLines.length) continue
            const data = dataLines.join('\n')
            if (data === '[DONE]') {
              onDone && onDone()
              controller.abort()
              return
            }
            onToken && onToken(data)
          }
        }

        // Trailing partial (unlikely to form complete event with data) ignored unless it has data: lines
        if (buffer.includes('data:')) {
          const lines = buffer.split(/\n/)
          const dataLines = []
          for (let raw of lines) {
            if (!raw) continue
            const line = raw.replace(/\r$/, '')
            if (line.startsWith('data:')) dataLines.push(line.slice(5))
          }
          if (dataLines.length) {
            const data = dataLines.join('\n')
            if (data && data !== '[DONE]') onToken && onToken(data)
          }
        }
        onDone && onDone()
      } catch (err) {
        if (controller.signal.aborted) return
        onError && onError(err)
        onDone && onDone()
      }
    })()

  return { cancel: () => controller.abort() }
}